/**
 * 导入模板和评定规则
 */
import * as XLSX from 'xlsx';

// 评定状态枚举
export enum EvaluationStatus {
  RED_UP = 'RED_UP',        // 红色向上箭头：高于高值10%以上
  ORANGE_UP = 'ORANGE_UP',  // 橙色向上箭头：高于高值10%以内
  GREEN_DOWN = 'GREEN_DOWN',// 绿色向下箭头：低于低值10%以内
  BLUE_DOWN = 'BLUE_DOWN'   // 蓝色向下箭头：低于低值10%以上
}

// 评定显示配置
export const EVALUATION_CONFIG = {
  [EvaluationStatus.RED_UP]: {
    color: '#f5222d',
    icon: '↗',
    text: '偏高',
    description: '高于高值10%以上'
  },
  [EvaluationStatus.ORANGE_UP]: {
    color: '#fa8c16',
    icon: '↗',
    text: '略高',
    description: '高于高值10%以内'
  },
  [EvaluationStatus.GREEN_DOWN]: {
    color: '#52c41a',
    icon: '↘',
    text: '略低',
    description: '低于低值10%以内'
  },
  [EvaluationStatus.BLUE_DOWN]: {
    color: '#1890ff',
    icon: '↘',
    text: '偏低',
    description: '低于低值10%以上'
  },
  // 后端状态兼容性配置
  'normal': {
    color: '#52c41a',
    icon: '↘',
    text: '正常',
    description: '在标准范围内'
  },
  'warning': {
    color: '#fa8c16',
    icon: '↗',
    text: '警告',
    description: '超出正常范围'
  },
  'danger': {
    color: '#f5222d',
    icon: '↗',
    text: '危险',
    description: '严重超出标准'
  }
} as const;

// 导入数据接口
export interface ImportRowData {
  companyName: string;          // 业务所属创大
  businessName: string;        // 培训业务名称
  elementName: string;         // 培训要素
  actualValue: number;         // 本次检查结果
  cityLevel: number;           // 主体分级
  responsiblePerson: string;   // 负责人
  checkDate: string;          // 检查时间
}

// 培训标准数据接口
export interface TrainingStandardData {
  id: number;
  business_id: number;
  element_id: number;
  business_name: string;
  element_name: string;
  city_level?: number;
  min_range?: number;
  max_range?: number;
  unit: string;
  is_active: number;
}

// 评定结果接口
export interface EvaluationResult {
  status: EvaluationStatus;
  referenceRange: string;
  evaluation?: string;
  remarks?: string;
}

/**
 * 讲师课酬评定规则
 * 一二线城市只对比最大值，其他城市比较范围的中间值
 */
export function evaluateTeacherFee(
  actualValue: number, 
  standard: TrainingStandardData, 
  cityLevel: number
): EvaluationResult {
  const isFirstSecondTier = cityLevel <= 2; // 一二线城市
  
  console.log('🔍 evaluateTeacherFee 详细调试:', {
    actualValue,
    cityLevel,
    isFirstSecondTier,
    standard: {
      min_range: standard.min_range,
      max_range: standard.max_range,
      element_name: standard.element_name
    }
  });
  
  if (isFirstSecondTier) {
    // 一二线城市对比最大值
    const maxValue = standard.max_range || 0;
    const diff = (actualValue - maxValue) / maxValue * 100;
    
    console.log('🔍 一二线城市计算:', {
      maxValue,
      diff: diff.toFixed(2) + '%'
    });
    
    if (diff > 10) {
      return {
        status: EvaluationStatus.RED_UP,
        referenceRange: `≤${maxValue}`,
        evaluation: `高于标准${diff.toFixed(1)}%`
      };
    } else if (diff > 0) {
      return {
        status: EvaluationStatus.ORANGE_UP,
        referenceRange: `≤${maxValue}`,
        evaluation: `高于标准${diff.toFixed(1)}%`
      };
    } else if (diff >= -10) {
      return {
        status: EvaluationStatus.GREEN_DOWN,
        referenceRange: `≤${maxValue}`,
        evaluation: `低于标准${Math.abs(diff).toFixed(1)}%`
      };
    } else {
      return {
        status: EvaluationStatus.BLUE_DOWN,
        referenceRange: `≤${maxValue}`,
        evaluation: `低于标准${Math.abs(diff).toFixed(1)}%`
      };
    }
  } else {
    // 其他城市对比中间值
    const minValue = standard.min_range || 0;
    const maxValue = standard.max_range || 0;
    const midValue = (minValue + maxValue) / 2;
    const diff = (actualValue - midValue) / midValue * 100;
    
    if (diff > 10) {
      return {
        status: EvaluationStatus.RED_UP,
        referenceRange: `${minValue}-${maxValue} (参考值: ${midValue})`,
        evaluation: `高于参考值${diff.toFixed(1)}%`
      };
    } else if (diff > 0) {
      return {
        status: EvaluationStatus.ORANGE_UP,
        referenceRange: `${minValue}-${maxValue} (参考值: ${midValue})`,
        evaluation: `高于参考值${diff.toFixed(1)}%`
      };
    } else if (diff >= -10) {
      return {
        status: EvaluationStatus.GREEN_DOWN,
        referenceRange: `${minValue}-${maxValue} (参考值: ${midValue})`,
        evaluation: `低于参考值${Math.abs(diff).toFixed(1)}%`
      };
    } else {
      return {
        status: EvaluationStatus.BLUE_DOWN,
        referenceRange: `${minValue}-${maxValue} (参考值: ${midValue})`,
        evaluation: `低于参考值${Math.abs(diff).toFixed(1)}%`
      };
    }
  }
}

/**
 * 其他培训要素评定规则
 * 不区分城市等级，统一按照范围评定
 */
export function evaluateOtherElement(
  actualValue: number,
  standard: TrainingStandardData
): EvaluationResult {
  const minValue = standard.min_range || 0;
  const maxValue = standard.max_range || 0;
  
  // 计算与高值和低值的差异百分比
  const highDiff = (actualValue - maxValue) / maxValue * 100;
  const lowDiff = (minValue - actualValue) / minValue * 100;
  
  if (actualValue > maxValue) {
    // 高于高值
    if (highDiff > 10) {
      return {
        status: EvaluationStatus.RED_UP,
        referenceRange: `${minValue}-${maxValue}`,
        evaluation: `高于高值${highDiff.toFixed(1)}%`
      };
    } else {
      return {
        status: EvaluationStatus.ORANGE_UP,
        referenceRange: `${minValue}-${maxValue}`,
        evaluation: `高于高值${highDiff.toFixed(1)}%`
      };
    }
  } else if (actualValue < minValue) {
    // 低于低值
    if (lowDiff > 10) {
      return {
        status: EvaluationStatus.BLUE_DOWN,
        referenceRange: `${minValue}-${maxValue}`,
        evaluation: `低于低值${lowDiff.toFixed(1)}%`
      };
    } else {
      return {
        status: EvaluationStatus.GREEN_DOWN,
        referenceRange: `${minValue}-${maxValue}`,
        evaluation: `低于低值${lowDiff.toFixed(1)}%`
      };
    }
  } else {
    // 在正常范围内
    const referenceRange = `${minValue}-${maxValue}`;
    console.log('🔍 正常范围内参考范围生成:', {
      minValue,
      maxValue,
      referenceRange,
      element_name: standard.element_name
    });
    return {
      status: EvaluationStatus.GREEN_DOWN, // 用绿色表示正常
      referenceRange: referenceRange,
      evaluation: '在正常范围内'
    };
  }
}

/**
 * 综合评定逻辑
 */
export function evaluateData(
  actualValue: number,
  standard: TrainingStandardData,
  cityLevel: number
): EvaluationResult {
  // 调试日志
  console.log('🔍 evaluateData 调试:', {
    actualValue,
    element_name: standard.element_name,
    cityLevel,
    min_range: standard.min_range,
    max_range: standard.max_range,
    isTeacherFee: standard.element_name.includes('讲师课酬') || standard.element_name.includes('师资费用')
  });
  
  // 判断是否为讲师课酬
  if (standard.element_name.includes('讲师课酬') || standard.element_name.includes('师资费用')) {
    const result = evaluateTeacherFee(actualValue, standard, cityLevel);
    console.log('🎯 讲师课酬评定结果:', result);
    return result;
  } else {
    const result = evaluateOtherElement(actualValue, standard);
    console.log('🎯 其他要素评定结果:', result);
    return result;
  }
}

/**
 * 生成导入模板
 */
export function generateImportTemplate(): ArrayBuffer {
  // 模板数据
  const templateData = [
    ['业务所属创大', '培训业务名称', '培训要素', '本次检查结果', '主体分级', '负责人', '检查时间'],
    ['北京华普', 'GYB培训', '讲师课酬', '700.00', '1', '张三', '2025年8月1日'],
    ['北京华普', 'GYB培训', '招生费用', '350.00', '1', '张三', '2025年8月1日'],
    ['北京华普', 'GYB培训', '场地费用', '3689.00', '1', '张三', '2025年8月1日'],
    ['北京华普', 'SYC学员班', '讲师课酬', '1200.00', '1', '张三', '2025年8月1日'],
    ['', '', '', '', '', '', ''],
    ['说明：'],
    ['1. 业务所属创大：填写公司名称'],
    ['2. 培训业务名称：如GYB培训、SYC学员班等'],
    ['3. 培训要素：如讲师课酬、招生费用、场地费用等'],
    ['4. 本次检查结果：填写具体数值'],
    ['5. 主体分级：1-一线城市，2-二线城市，3-三线城市，4-四线城市，5-五线城市'],
    ['6. 负责人：填写责任人姓名'],
    ['7. 检查时间：格式如"2025年8月1日"或"2025-08-01"']
  ];

  // 创建工作簿
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(templateData);

  // 设置列宽
  ws['!cols'] = [
    { width: 15 }, // 业务所属创大
    { width: 15 }, // 培训业务名称
    { width: 12 }, // 培训要素
    { width: 12 }, // 本次检查结果
    { width: 10 }, // 主体分级
    { width: 10 }, // 负责人
    { width: 15 }  // 检查时间
  ];

  // 添加工作表
  XLSX.utils.book_append_sheet(wb, ws, '导入模板');

  // 生成文件
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

/**
 * 下载导入模板
 */
export function downloadImportTemplate() {
  const buffer = generateImportTemplate();
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = '检查报告导入模板.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 解析导入的Excel文件
 */
export function parseImportFile(file: File): Promise<ImportRowData[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // 跳过标题行，解析数据
        const rows = jsonData.slice(1) as any[][];
        const result: ImportRowData[] = [];
        
        rows.forEach((row, index) => {
          // 跳过空行、说明行和无效行
          if (!row || row.length === 0 || !row[0]) {
            return;
          }
          
          const firstCell = row[0].toString().trim();
          
          // 跳过说明行和空行
          if (firstCell === '' || 
              firstCell.includes('说明') || 
              firstCell.includes('业务所属创大') ||
              row.every(cell => !cell || cell.toString().trim() === '')) {
            return;
          }
          
          // 跳过数字开头的说明行（如"1. 业务所属创大"）
          if (/^\d+\./.test(firstCell)) {
            return;
          }
          
          try {
            // 处理检查日期：如果是数字，则转换Excel序列号为日期
            let checkDate = row[6]?.toString().trim() || '';
            if (checkDate && !isNaN(Number(checkDate))) {
              const excelSerialNumber = Number(checkDate);
              
              // Excel日期转换：Excel将1900年1月1日作为序列号1
              // 但Excel错误地认为1900年是闰年，所以需要特殊处理
              let actualDate: Date;
              
              if (excelSerialNumber < 61) {
                // 1900年3月1日之前，需要减去1天来补偿Excel的闰年bug
                actualDate = new Date(1900, 0, excelSerialNumber);
              } else {
                // 1900年3月1日及之后，减去2天（1天是基础偏移，1天是闰年bug补偿）
                actualDate = new Date(1900, 0, excelSerialNumber - 1);
              }
              
              checkDate = actualDate.toISOString().split('T')[0]; // 使用 YYYY-MM-DD 格式
              
              console.log('🔍 Excel日期转换调试:', {
                原始序列号: excelSerialNumber,
                转换方法: excelSerialNumber < 61 ? '早期日期' : '标准日期',
                计算结果: actualDate,
                格式化结果: checkDate
              });
            }
            
            const rowData: ImportRowData = {
              companyName: row[0]?.toString().trim() || '',
              businessName: row[1]?.toString().trim() || '',
              elementName: row[2]?.toString().trim() || '',
              actualValue: parseFloat(row[3]?.toString() || '0'),
              cityLevel: parseInt(row[4]?.toString() || '1'),
              responsiblePerson: row[5]?.toString().trim() || '',
              checkDate: checkDate
            };
            
            // 调试日志
            console.log(`🔍 第${index + 2}行解析结果:`, {
              原始第7列: row[6],
              是否为数字: !isNaN(Number(row[6]?.toString() || '')),
              解析后checkDate: rowData.checkDate,
              所有列: row
            });
            
            // 验证必填字段
            if (!rowData.companyName || !rowData.businessName || !rowData.elementName) {
              return; // 跳过不完整的行，不报错
            }
            
            // 验证数据有效性
            if (isNaN(rowData.actualValue) || rowData.actualValue <= 0) {
              return; // 跳过无效数值
            }
            
            result.push(rowData);
          } catch (error) {
            // 静默跳过解析失败的行
            console.debug(`跳过第${index + 2}行数据:`, error);
          }
        });
        
        resolve(result);
      } catch (error) {
        reject(new Error('文件解析失败: ' + (error as Error).message));
      }
    };
    
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsArrayBuffer(file);
  });
}